import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import './App.css';

import { SiteSettingsProvider } from './lib/SiteSettingsContext';
import { AuthProvider } from './lib/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import FloatingButtons from './components/FloatingButtons';
import GlobalEnquiryProvider from './components/EnquiryPopup/GlobalEnquiryProvider';
import ScrollToTop from './components/ScrollToTop';

// Public pages — lazy-loaded for faster initial paint & smaller bundle
const Home = lazy(() => import('./pages/Home'));
const Listing = lazy(() => import('./pages/Listing'));
const PropertyDetail = lazy(() => import('./pages/PropertyDetail'));
const CityPage = lazy(() => import('./pages/CityPage'));
const DeveloperPage = lazy(() => import('./pages/DeveloperPage'));
const ToolsPage = lazy(() => import('./pages/ToolsPage'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogDetail = lazy(() => import('./pages/BlogDetail'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const LocalityPage = lazy(() => import('./pages/LocalityPage'));
const CitySegmentPage = lazy(() => import('./pages/CitySegmentPage'));
const CityNearbyPage = lazy(() => import('./pages/CityNearbyPage'));
const CityNewLaunchPage = lazy(() => import('./pages/CityNewLaunchPage'));
const ComparePage = lazy(() => import('./pages/ComparePage'));
const GuidesPage = lazy(() => import('./pages/GuidesPage'));
const DevelopersIndex = lazy(() => import('./pages/DevelopersIndex'));
const SitemapPage = lazy(() => import('./pages/SitemapPage'));

// Auth & registration flow
const Register = lazy(() => import('./pages/Register'));
const RegisterForm = lazy(() => import('./pages/RegisterForm'));
const VerifyOtp = lazy(() => import('./pages/VerifyOtp'));
const PendingApproval = lazy(() => import('./pages/PendingApproval'));
const Login = lazy(() => import('./pages/Login'));

// User dashboard (Phase 2)
const DashboardLayout = lazy(() => import('./dashboard/DashboardLayout'));
const DashboardOverview = lazy(() => import('./dashboard/DashboardOverview'));
const MyListings = lazy(() => import('./dashboard/MyListings'));
const MyProfile = lazy(() => import('./dashboard/MyProfile'));
const ListingForm = lazy(() => import('./dashboard/ListingForm'));

// Admin — lazy-loaded so admin code never lands on a public visitor's device
import ProtectedRoute from './admin/ProtectedRoute';
const AdminLogin = lazy(() => import('./admin/AdminLogin'));
const AdminLayout = lazy(() => import('./admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./admin/AdminDashboard'));
const PropertiesList = lazy(() => import('./admin/PropertiesList'));
const LeadsList = lazy(() => import('./admin/LeadsList'));
const BannersList = lazy(() => import('./admin/BannersList'));
const BlogsList = lazy(() => import('./admin/BlogsList'));
const TestimonialsList = lazy(() => import('./admin/TestimonialsList'));
const CitiesList = lazy(() => import('./admin/CitiesList'));
const LocalitiesList = lazy(() => import('./admin/LocalitiesList'));
const DevelopersList = lazy(() => import('./admin/DevelopersList'));
const PageCMSPage = lazy(() => import('./admin/PageCMSPage'));
const SiteSettingsPage = lazy(() => import('./admin/SiteSettingsPage'));
const UsersList = lazy(() => import('./admin/UsersList'));
const UserListingsAdmin = lazy(() => import('./admin/UserListingsAdmin'));
const ProgrammaticPagesList = lazy(() => import('./admin/ProgrammaticPagesList'));
const StatesList = lazy(() => import('./admin/StatesList'));
const ImportReportsPage = lazy(() => import('./admin/ImportReportsPage'));

function RouteFallback() {
    return (
        <div className="min-h-screen flex items-center justify-center bg-[#FAFAF7]" data-testid="route-fallback">
            <div className="w-10 h-10 border-2 border-[#1B4332]/15 border-t-[#E07B39] rounded-full animate-spin" />
        </div>
    );
}

function PublicLayout({ children }) {
    return (
        <>
            <Navbar />
            <main className="flex-1">{children}</main>
            <Footer />
            <FloatingButtons />
            <GlobalEnquiryProvider />
        </>
    );
}

function AppShell() {
    const location = useLocation();
    const isAdmin = location.pathname.startsWith('/admin');
    const isDashboard = location.pathname.startsWith('/dashboard');

    if (isAdmin) {
        return (
            <Suspense fallback={<RouteFallback />}>
                <Routes>
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                        <Route path="/admin" element={<AdminDashboard />} />
                        <Route path="/admin/properties" element={<PropertiesList />} />
                        <Route path="/admin/import-reports" element={<ImportReportsPage />} />
                        <Route path="/admin/leads" element={<LeadsList />} />
                        <Route path="/admin/page-cms" element={<PageCMSPage />} />
                        <Route path="/admin/banners" element={<BannersList />} />
                        <Route path="/admin/blogs" element={<BlogsList />} />
                        <Route path="/admin/cities" element={<CitiesList />} />
                        <Route path="/admin/localities" element={<LocalitiesList />} />
                        <Route path="/admin/developers" element={<DevelopersList />} />
                        <Route path="/admin/testimonials" element={<TestimonialsList />} />
                        <Route path="/admin/users" element={<UsersList />} />
                        <Route path="/admin/listings" element={<UserListingsAdmin />} />
                        <Route path="/admin/programmatic-pages" element={<ProgrammaticPagesList />} />
                        <Route path="/admin/states" element={<StatesList />} />
                        <Route path="/admin/settings" element={<SiteSettingsPage />} />
                    </Route>
                </Routes>
            </Suspense>
        );
    }

    if (isDashboard) {
        return (
            <Suspense fallback={<RouteFallback />}>
                <Routes>
                    <Route element={<DashboardLayout />}>
                        <Route path="/dashboard" element={<DashboardOverview />} />
                        <Route path="/dashboard/listings" element={<MyListings />} />
                        <Route path="/dashboard/listings/new" element={<ListingForm />} />
                        <Route path="/dashboard/listings/:id/edit" element={<ListingForm />} />
                        <Route path="/dashboard/profile" element={<MyProfile />} />
                    </Route>
                </Routes>
            </Suspense>
        );
    }

    return (
        <PublicLayout>
            <Suspense fallback={<RouteFallback />}>
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/buy" element={<Listing listingType="BUY" />} />
                    <Route path="/rent" element={<Listing listingType="RENT" />} />
                    <Route path="/new-projects" element={<Listing listingType="NEW_PROJECT" />} />
                    <Route path="/pg" element={<Listing listingType="PG" />} />
                    <Route path="/plots" element={<Listing listingType="PLOT" />} />
                    <Route path="/resale" element={<Listing listingType="RESALE" />} />
                    <Route path="/commercial" element={<Listing listingType="COMMERCIAL" />} />
                    <Route path="/property/:slug" element={<PropertyDetail />} />
                    <Route path="/city/:citySlug/near/:landmarkSlug" element={<CityNearbyPage />} />
                    <Route path="/city/:citySlug/new-launch-projects" element={<CityNewLaunchPage />} />
                    <Route path="/city/:citySlug/:segment" element={<CitySegmentPage />} />
                    <Route path="/city/:slug" element={<CityPage />} />
                    <Route path="/locality/:slug" element={<LocalityPage />} />
                    <Route path="/compare/:slug" element={<ComparePage />} />
                    <Route path="/guides" element={<GuidesPage />} />
                    <Route path="/guides/:slug" element={<GuidesPage />} />
                    <Route path="/developers" element={<DevelopersIndex />} />
                    <Route path="/sitemap" element={<SitemapPage />} />
                    <Route path="/developer/:slug" element={<DeveloperPage />} />
                    <Route path="/tools" element={<ToolsPage />} />
                    <Route path="/tools/emi-calculator" element={<ToolsPage initialTab="emi" />} />
                    <Route path="/tools/stamp-duty" element={<ToolsPage initialTab="stamp-duty" />} />
                    <Route path="/tools/vastu-checker" element={<ToolsPage initialTab="vastu" />} />
                    <Route path="/tools/area-converter" element={<ToolsPage initialTab="area" />} />
                    <Route path="/tools/rental-yield" element={<ToolsPage initialTab="rental-yield" />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:slug" element={<BlogDetail />} />
                    <Route path="/about" element={<Navigate to="/about-us" replace />} />
                    <Route path="/about-us" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/register/verify-otp" element={<VerifyOtp />} />
                    <Route path="/register/:role" element={<RegisterForm />} />
                    <Route path="/pending-approval" element={<PendingApproval />} />
                    <Route path="/login" element={<Login />} />
                </Routes>
            </Suspense>
        </PublicLayout>
    );
}

function App() {
    return (
        <div className="App min-h-screen flex flex-col">
            <ErrorBoundary>
                <SiteSettingsProvider>
                    <BrowserRouter>
                        <AuthProvider>
                            <ScrollToTop />
                            <AppShell />
                        </AuthProvider>
                    </BrowserRouter>
                </SiteSettingsProvider>
                <Toaster position="top-right" richColors />
            </ErrorBoundary>
        </div>
    );
}

export default App;
